import { useState, useEffect, lazy, Suspense } from "react";
import { TAB } from "./retirementPlanningTabs";

const RetirementPlanning = lazy(() => import("./RetirementPlanning"));

/**
 * PlanViewer — public read-only view of a shared retirement plan.
 *
 * URL: /plan?token=xxx
 * Loads a self-contained snapshot from /api/plan/:token (no login) and renders
 * the existing RetirementPlanning component in read-only mode, limited to the
 * קצבאות + תזרים tabs. The client can adjust incomes & returns in the cash-flow
 * table — changes are local-only (never persisted, never sent back to the advisor).
 */
export default function PlanViewer() {
  const token = new URLSearchParams(window.location.search).get("token");

  const [phase, setPhase] = useState("checking"); // checking | ready | error
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setError("קישור לא תקין."); setPhase("error"); return; }
    let cancelled = false;
    fetch(`/api/plan/${encodeURIComponent(token)}`)
      .then(async (r) => {
        const data = await r.json();
        if (!r.ok) throw new Error(data.error || "load failed");
        return data;
      })
      .then((data) => { if (!cancelled) { setPayload(data); setPhase("ready"); } })
      .catch((e) => {
        if (cancelled) return;
        console.error("[PlanViewer] load error:", e);
        setError(e.message || "הקישור אינו תקף או שפג תוקפו.");
        setPhase("error");
      });
    return () => { cancelled = true; };
  }, [token]);

  if (phase === "checking") {
    return <FullPage>טוען תכנון…</FullPage>;
  }

  if (phase === "error") {
    return (
      <FullPage>
        <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
        <div style={{ color: "#dc2626", fontWeight: 700, fontSize: 16 }}>{error}</div>
        <div style={{ color: "#64748b", fontSize: 13, marginTop: 8 }}>בקש מהיועץ קישור חדש.</div>
      </FullPage>
    );
  }

  // ready
  const plan = payload.plan || {};
  // fundsB נגזר ב-RetirementPlanning מ-clients.find(c => c.id === selectedClientB).
  // נבנה רשומת לקוח סינתטית כדי שהלוגיקה הקיימת תמצא את כספי בן/בת הזוג.
  const syntheticClients = plan.selectedClientB
    ? [{ id: plan.selectedClientB, funds: payload.fundsB || [], clientName: "בן/בת זוג" }]
    : [];

  return (
    <Suspense fallback={<FullPage>טוען תכנון…</FullPage>}>
      <RetirementPlanning
        funds={payload.funds || []}
        initialData={plan}
        clientName={payload.clientName || ""}
        clientId={payload.clientId || ""}
        clients={syntheticClients}
        plans={[]}
        readOnly={true}
        allowedTabs={[TAB.PENSIONS, TAB.PROJECTION]}
        inline={false}
        onClose={() => {}}
      />
    </Suspense>
  );
}

function FullPage({ children }) {
  return (
    <div style={{
      minHeight: "100vh", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", textAlign: "center",
      background: "linear-gradient(150deg,#f1f5f9,#e9eef8)", direction: "rtl",
      fontFamily: "'Segoe UI', sans-serif", color: "#64748b", padding: 24,
    }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>🏖️</div>
      <div>{children}</div>
    </div>
  );
}
